import Loading from "components/center-loading";
import { TextSpan } from "components/text-label";
import { useToastContext } from "hooks/useToastContext";
import moment from "moment";
import { useCallback, useState } from "react";
import intl from "react-intl-universal";
import { getDailyDeliveriesAsync } from "services/daily-delivery.service";
import { ISO_DATE_FORMAT } from "utils/format-date";
import { TableEmpty } from "./components";
import { DailyDeliveryFilters } from "./components/Filter";
import { DailyDeliveryTable } from "./components/Table/DailyDeliveryTable";
import { Container, FiltersWrapper } from "./styled";
import type { BreweryTableData, FilterState } from "./types";
import { pivotByBrewery } from "./utils";

export const DailyDelivery = () => {
  const { openToast } = useToastContext();

  const [breweryTableData, setBreweryTableData] = useState<BreweryTableData>({
    columns: ["Brewery"],
    rows: [],
  });

  const [filter, setFilter] = useState<FilterState>({
    _minDeliveryDate: moment().format(ISO_DATE_FORMAT),
    _maxDeliveryDate: moment().add(10, "days").format(ISO_DATE_FORMAT),
    FamilyIds: undefined,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const handleChangeFilter = useCallback((prop: string, value: string | number[] | undefined) => {
    setFilter((prev) => ({ ...prev, [prop]: value }));
    
    if (prop === "FamilyIds" && !value) {
      setHasSearched(false);
      setBreweryTableData({
        columns: ["Brewery"],
        rows: [],
      });
    }
  }, []);

  const handleLoadDailyDeliveryAsync = useCallback(
    async (filterState: FilterState) => {
      // Validate required dates
      if (!filterState._minDeliveryDate || !filterState._maxDeliveryDate) {
        openToast(intl.get("dailyDelivery.datesRequired"), "negative");
        return;
      }

      if (!filterState.FamilyIds || filterState.FamilyIds.trim() === "") {
        setBreweryTableData({
          columns: ["Brewery"],
          rows: [],
        });
        return;
      }

      try {
        setIsLoading(true);
        setHasSearched(true);
        const dados = await getDailyDeliveriesAsync(filterState);

        if (dados && dados.length > 0) {
          const tableData = pivotByBrewery(dados);
          setBreweryTableData(tableData);
        } else {
          setBreweryTableData({
            columns: ["Brewery"],
            rows: [],
          });
        }
      } catch {
        openToast(intl.get("dailyDelivery.errorLoadingDailyDelivery"), "negative");
        setBreweryTableData({
          columns: ["Brewery"],
          rows: [],
        });
      } finally {
        setIsLoading(false);
      }
    },
    [openToast]
  );

  const handleSearchDailyDeliveries = useCallback(() => {
    handleLoadDailyDeliveryAsync(filter);
  }, [handleLoadDailyDeliveryAsync, filter]);

  const renderTableContent = () => {
    if (breweryTableData?.rows?.length > 0) {
      return (
        <DailyDeliveryTable
          breweryTableData={breweryTableData}
          familyId={Number(filter.FamilyIds)}
          handleSearchDailyDeliveries={handleSearchDailyDeliveries}
        />
      );
    }

    if (hasSearched && breweryTableData?.rows?.length === 0) {
      return <TableEmpty />;
    }

    return null;
  };

  return (
    <Container>
      <TextSpan
        color="var(-color-brand-600)"
        display="block"
        style={{ fontSize: "32px", fontWeight: 700 }}
      >
        {intl.get("dailyDelivery.title")}
      </TextSpan>
      <FiltersWrapper>
        <DailyDeliveryFilters
          filter={filter}
          onChangeFilter={handleChangeFilter}
          searchDailyDeliveries={handleSearchDailyDeliveries}
        />
      </FiltersWrapper>
      {renderTableContent()}
      <Loading isLoading={isLoading} />
    </Container>
  );
};

export default DailyDelivery;
